import React, { ChangeEvent, LegacyRef, useMemo } from 'react';
import NumberFormat, { NumberFormatProps } from 'react-number-format';
import { useSelector } from 'react-redux';
import Input, { InputProps } from 'housecall-ui/dist/DesignSystem/Inputs/TextFields/Input';
import { debounce } from 'lodash-es';
import { convertStringToNumber } from 'utils/currency';

import AuthorizationService from 'components/AdminNew/Models/AuthorizationService';
import CurrencyDisplay from 'components/Common/Currency/CurrencyDisplay';
import { PayWallDuration, PayWallFullState } from 'components/PayWall/Redux/Reducer.type';
import { Feature, OnFeatureToggle } from 'components/PayWall/types';

import { PriceDisplay } from './PayWallPlanSummary';

const NumberFormatCustom: React.ElementType<NumberFormatProps & { inputRef: LegacyRef<NumberFormat> }> = (props) => {
  const { inputRef, ...other } = props;

  return (
    <NumberFormat
      {...other}
      ref={inputRef}
      decimalScale={2}
      fixedDecimalScale
      thousandSeparator
      allowNegative={false}
      prefix="$"
    />
  );
};

type Props = {
  permission?: unknown;
  feature: Feature;
  onFeatureToggle: OnFeatureToggle;
};

export default function EditPriceTextField(props: Props): JSX.Element {
  const { permission, feature, onFeatureToggle } = props;
  const employee = useSelector((state: PayWallFullState) => state.employee);
  const allowEdit = AuthorizationService.isAuthorized(employee, permission || 'change feature price');

  const { included = false } = feature;

  const handleChange = useMemo(
    () =>
      debounce((value: string) => {
        const newFeaturePrice = convertStringToNumber(value) * 100;

        if (newFeaturePrice !== feature.price) {
          onFeatureToggle(feature.featureName, newFeaturePrice, true);
        }
      }, 1000),
    [feature, onFeatureToggle]
  );

  if (included) {
    return (
      <PriceDisplay
        price={<CurrencyDisplay amountInCents={feature.price} />}
        duration={PayWallDuration.Monthly}
        strikeThrough
      />
    );
  }

  return (
    <>
      {allowEdit && (
        <Input
          value={feature.price / 100.0}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            handleChange(event.target.value);
          }}
          id="custom-price"
          inputComponent={NumberFormatCustom as NonNullable<InputProps['inputComponent']>}
          disabled={!feature.enabled}
        />
      )}
      {!allowEdit && (
        <PriceDisplay price={<CurrencyDisplay amountInCents={feature.price} />} duration={PayWallDuration.Monthly} />
      )}
    </>
  );
}
